/**
 * @description Apply tenant web-identity to document.head (tab + progressive
 * home-screen / share tags). Mirrors workers/lib/web-identity.cjs.
 *
 * @dependencies none
 *
 * @notes iOS Add to Home Screen reads apple-mobile-web-app-title + manifest
 * name, not this module. The Worker HTMLRewriter is SoT on SO hosts. This is
 * progressive enhancement for LynxPay (no Worker) and already-open tabs.
 * On *.lynx.build, apple-touch-icon / og:image point at composed Worker
 * URLs with the same ?v= shareCardVersion the Worker stamps — never the
 * bare path (that double-fetches after first HTML).
 * Empty CP/SO favicon stays unset — never LynxPay.
 */

const RASTER_EXT = /\.(png|jpe?g|webp)$/i;
const SHARE_EXT = /\.(png|jpe?g|webp|gif)$/i;
const SHARE_BG_FALLBACK = '#0a0a0b';
const ICON_BG = '#ffffff';
const LIGHT_BG_MAX_LUMINANCE = 0.82;

/**
 * @param {*} value
 * @returns {boolean}
 */
export function isHttpsUrl(value) {
  if (typeof value !== 'string' || !value) return false;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:';
  } catch (_err) {
    return false;
  }
}

/**
 * @param {string} url
 * @returns {string}
 */
function urlPath(url) {
  try {
    return new URL(url).pathname;
  } catch (_err) {
    return String(url).split('?')[0];
  }
}

/**
 * @param {*} url
 * @returns {boolean}
 */
export function isHomeScreenIconUrl(url) {
  return isHttpsUrl(url) && RASTER_EXT.test(urlPath(url));
}

/**
 * @param {*} url
 * @returns {boolean}
 */
export function isShareImageUrl(url) {
  return isHttpsUrl(url) && SHARE_EXT.test(urlPath(url));
}

/**
 * @param {*} value
 * @returns {string|null}
 */
function sanitizeHexColor(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (/^#([0-9A-Fa-f]{6})$/.test(trimmed)) return trimmed.toLowerCase();
  if (/^#([0-9A-Fa-f]{3})$/.test(trimmed)) {
    return `#${trimmed[1]}${trimmed[1]}${trimmed[2]}${trimmed[2]}${trimmed[3]}${trimmed[3]}`.toLowerCase();
  }
  return null;
}

/**
 * @param {string} hex
 * @returns {number}
 */
function relativeLuminance(hex) {
  const color = sanitizeHexColor(hex);
  if (!color) return 0;
  const channel = (start) => {
    const value = parseInt(color.slice(start, start + 2), 16) / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
}

/**
 * @param {*} primaryColor
 * @returns {string}
 */
export function pickShareBackground(primaryColor) {
  const hex = sanitizeHexColor(primaryColor);
  if (!hex) return SHARE_BG_FALLBACK;
  if (relativeLuminance(hex) > LIGHT_BG_MAX_LUMINANCE) return SHARE_BG_FALLBACK;
  return hex;
}

/**
 * iOS default A2HS plate. Always white — tenant primary behind a brand-colored
 * mark is color-on-color in Light appearance.
 * @returns {string}
 */
export function pickIconBackground() {
  return ICON_BG;
}

/**
 * True on Worker-fronted SO/CP hosts that serve /apple-touch-icon.png.
 * @param {string} [hostname]
 * @returns {boolean}
 */
export function isSoAppHostname(hostname) {
  return String(hostname || '').toLowerCase().endsWith('.lynx.build');
}

/**
 * Same-origin composed head URLs the Worker stamps. Always include ?v=.
 *
 * @param {string} origin
 * @param {object} identity
 * @returns {{ appleTouchIconUrl: string, ogImageUrl: string }|null}
 */
export function soComposedHeadUrls(origin, identity) {
  if (typeof origin !== 'string' || !origin) return null;
  const version = shareCardVersion(identity);
  return {
    appleTouchIconUrl: `${origin}/apple-touch-icon.png?v=${version}`,
    ogImageUrl: `${origin}/og-image.png?v=${version}`
  };
}

/**
 * Cache-buster for composed Worker URLs (not an id). Must match
 * workers/lib/web-identity.cjs shareCardVersion.
 *
 * @param {object} identity
 * @returns {string}
 */
export function shareCardVersion(identity) {
  const safe = identity && typeof identity === 'object' ? identity : {};
  const key = [
    safe.ogImageUrl || '',
    safe.appleTouchIconUrl || '',
    safe.shareBackground || '',
    safe.iconBackground || '',
    safe.name || ''
  ].join('|');
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = ((hash << 5) - hash + key.charCodeAt(i)) | 0;
  }
  return (hash >>> 0).toString(36);
}

/**
 * Resolve the same jobs as the Worker: tab / home screen / share card.
 *
 * @param {object|null|undefined} branding - overlay-merged branding
 * @param {string|null} [organizationName]
 * @returns {{
 *   name: string|null,
 *   description: string|null,
 *   themeColor: string|null,
 *   primaryColor: string|null,
 *   shareBackground: string,
 *   iconBackground: string,
 *   faviconUrl: string|null,
 *   appleTouchIconUrl: string|null,
 *   ogImageUrl: string|null
 * }}
 */
export function resolveWebIdentity(branding, organizationName) {
  const root = branding && typeof branding === 'object' ? branding : {};
  const assets = root.assets && typeof root.assets === 'object' ? root.assets : {};
  const identity = root.identity && typeof root.identity === 'object' ? root.identity : {};
  const meta = root.meta && typeof root.meta === 'object' ? root.meta : {};
  const colors = root.colors && typeof root.colors === 'object' ? root.colors : {};
  const platformName = typeof identity.platformName === 'string' ? identity.platformName.trim() : '';
  const org = typeof organizationName === 'string' ? organizationName.trim() : '';
  const name = platformName || org || null;
  const primaryColor = sanitizeHexColor(colors.primaryColor);
  const faviconUrl = isHttpsUrl(assets.faviconUrl) ? assets.faviconUrl : null;
  const logoMarkUrl = isHttpsUrl(assets.logoMarkUrl) ? assets.logoMarkUrl : null;
  const logoUrl = isHttpsUrl(assets.logoUrl) ? assets.logoUrl : null;

  return {
    name,
    description: typeof meta.description === 'string' && meta.description.trim()
      ? meta.description.trim()
      : (name ? `${name} — Business payments made simple` : null),
    themeColor: typeof meta.themeColor === 'string' && meta.themeColor.trim()
      ? meta.themeColor.trim()
      : null,
    primaryColor,
    shareBackground: pickShareBackground(primaryColor),
    iconBackground: pickIconBackground(),
    faviconUrl,
    appleTouchIconUrl: isHomeScreenIconUrl(logoMarkUrl)
      ? logoMarkUrl
      : (isHomeScreenIconUrl(faviconUrl) ? faviconUrl : null),
    ogImageUrl: isShareImageUrl(logoMarkUrl)
      ? logoMarkUrl
      : (isShareImageUrl(logoUrl) ? logoUrl : null)
  };
}

/**
 * Set or create a link[rel] href.
 * @param {string} rel
 * @param {string} href
 */
function setLinkRel(rel, href) {
  const selector = rel === 'icon' ? "link[rel~='icon']" : `link[rel='${rel}']`;
  let link = document.querySelector(selector);
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.appendChild(link);
  }
  link.href = href;
}

/**
 * Set or create a meta tag.
 * @param {'name'|'property'} attr
 * @param {string} key
 * @param {string} content
 */
function setMeta(attr, key, content) {
  let meta = document.querySelector(`meta[${attr}="${key}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attr, key);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

/**
 * Progressive document.head apply. No-ops missing URLs (letter fallback stays).
 * @param {object} identity - resolveWebIdentity() result
 */
export function applyDocumentWebIdentity(identity) {
  if (typeof document === 'undefined' || !identity) return;
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const composed = isSoAppHostname(hostname) ? soComposedHeadUrls(origin, identity) : null;
  if (identity.faviconUrl) setLinkRel('icon', identity.faviconUrl);
  const shareImage = composed ? composed.ogImageUrl : identity.ogImageUrl;
  if (composed) {
    setLinkRel('apple-touch-icon', composed.appleTouchIconUrl);
  } else if (identity.appleTouchIconUrl) {
    setLinkRel('apple-touch-icon', identity.appleTouchIconUrl);
  }
  if (shareImage) {
    setMeta('property', 'og:image', shareImage);
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:image', shareImage);
  }
  if (identity.name) {
    setMeta('name', 'apple-mobile-web-app-title', identity.name);
    setMeta('name', 'application-name', identity.name);
    setMeta('property', 'og:title', identity.name);
    setMeta('property', 'og:site_name', identity.name);
    setMeta('name', 'twitter:title', identity.name);
  }
  if (identity.themeColor) setMeta('name', 'theme-color', identity.themeColor);
  if (identity.description) {
    setMeta('name', 'description', identity.description);
    setMeta('property', 'og:description', identity.description);
    setMeta('name', 'twitter:description', identity.description);
  }
}
